import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInterview } from '@features/interview/context/InterviewContext';
import { useMedia } from '@features/media/hooks/useMedia';
import { useNetwork } from '@features/network/hooks/useNetwork';
import { ROUTES, INTERVIEW_PHASES } from '@shared/utils/constants';
import { Button, Toast, Spinner } from '@shared/components/ui/UI';
import { VideoPreview } from '@features/media/components/VideoPreview';
import {
  CameraIcon,
  MicrophoneIcon,
  WifiIcon,
  WifiOffIcon,
  PlayIcon,
  StopIcon
} from '@shared/components/icons';

// AI Avatar Component for Live Preview - Professional AI Interviewer Icon
const LivePreviewAvatar = ({ isSpeaking = false }) => {
  return (
    <div className="relative w-full h-full overflow-hidden rounded-2xl bg-gradient-to-br from-slate-800 via-slate-900 to-slate-950">
      {/* Animated tech background */}
      <div className="absolute inset-0">
        <svg className="absolute inset-0 w-full h-full opacity-30" viewBox="0 0 400 400" preserveAspectRatio="xMidYMid slice">
          <defs>
            <linearGradient id="lineGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#60a5fa" stopOpacity="0.6" />
              <stop offset="50%" stopColor="#818cf8" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#a78bfa" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <g stroke="url(#lineGrad)" strokeWidth="0.5" fill="none">
            {/* Circuit patterns */}
            <path d="M0,50 H100 L120,70 H200 L220,50 H300" className="animate-pulse" />
            <path d="M400,100 H300 L280,120 H180 L160,100 H100" className="animate-pulse" style={{ animationDelay: '0.3s' }} />
            <path d="M0,150 H80 L100,170 H160" className="animate-pulse" style={{ animationDelay: '0.6s' }} />
            <path d="M400,200 H350 L330,180 H250" className="animate-pulse" style={{ animationDelay: '0.9s' }} />
            <path d="M0,250 H120 L140,270 H220 L240,250 H320" className="animate-pulse" style={{ animationDelay: '1.2s' }} />
            <path d="M400,300 H280 L260,320 H180 L160,300 H80" className="animate-pulse" style={{ animationDelay: '1.5s' }} />
            <path d="M0,350 H150 L170,370 H280" className="animate-pulse" style={{ animationDelay: '1.8s' }} />

            {/* Connection nodes */}
            <circle cx="100" cy="50" r="3" fill="#60a5fa" className="animate-pulse" />
            <circle cx="220" cy="50" r="3" fill="#818cf8" className="animate-pulse" style={{ animationDelay: '0.2s' }} />
            <circle cx="300" cy="100" r="3" fill="#60a5fa" className="animate-pulse" style={{ animationDelay: '0.4s' }} />
            <circle cx="160" cy="100" r="3" fill="#a78bfa" className="animate-pulse" style={{ animationDelay: '0.6s' }} />
            <circle cx="80" cy="150" r="3" fill="#818cf8" className="animate-pulse" style={{ animationDelay: '0.8s' }} />
            <circle cx="350" cy="200" r="3" fill="#60a5fa" className="animate-pulse" style={{ animationDelay: '1s' }} />
            <circle cx="240" cy="250" r="3" fill="#a78bfa" className="animate-pulse" style={{ animationDelay: '1.2s' }} />
            <circle cx="160" cy="300" r="3" fill="#818cf8" className="animate-pulse" style={{ animationDelay: '1.4s' }} />
          </g>
        </svg>
      </div>

      {/* AI Interviewer Icon */}
      <div className="absolute inset-0 flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          className="relative"
        >
          {/* Glow effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-indigo-500/30 rounded-full blur-3xl scale-150" />

          {/* Professional AI Interviewer Icon */}
          <div className="relative w-48 h-48 flex items-center justify-center">
            <svg viewBox="0 0 120 120" className="w-full h-full drop-shadow-2xl">
              <defs>
                <linearGradient id="aiGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#3b82f6" />
                  <stop offset="50%" stopColor="#6366f1" />
                  <stop offset="100%" stopColor="#8b5cf6" />
                </linearGradient>
                <linearGradient id="innerGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor="#1e3a5f" />
                  <stop offset="100%" stopColor="#1e1b4b" />
                </linearGradient>
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>

              {/* Outer ring */}
              <circle cx="60" cy="60" r="55" fill="none" stroke="url(#aiGrad)" strokeWidth="2" opacity="0.6" />
              <circle cx="60" cy="60" r="50" fill="none" stroke="url(#aiGrad)" strokeWidth="1" opacity="0.4" />

              {/* Main head shape - stylized AI head */}
              <circle cx="60" cy="55" r="38" fill="url(#innerGrad)" />

              {/* AI Brain pattern */}
              <g filter="url(#glow)" opacity="0.9">
                {/* Central processor */}
                <rect x="50" y="45" width="20" height="20" rx="3" fill="none" stroke="#60a5fa" strokeWidth="1.5" />
                <rect x="54" y="49" width="12" height="12" rx="2" fill="#3b82f6" opacity="0.6" />

                {/* Neural connections */}
                <path d="M50,55 L35,45" stroke="#60a5fa" strokeWidth="1" />
                <path d="M50,55 L35,65" stroke="#60a5fa" strokeWidth="1" />
                <path d="M70,55 L85,45" stroke="#60a5fa" strokeWidth="1" />
                <path d="M70,55 L85,65" stroke="#60a5fa" strokeWidth="1" />
                <path d="M60,45 L60,32" stroke="#60a5fa" strokeWidth="1" />
                <path d="M60,65 L60,78" stroke="#60a5fa" strokeWidth="1" />

                {/* Neural nodes */}
                <circle cx="35" cy="45" r="4" fill="#818cf8" />
                <circle cx="35" cy="65" r="4" fill="#818cf8" />
                <circle cx="85" cy="45" r="4" fill="#818cf8" />
                <circle cx="85" cy="65" r="4" fill="#818cf8" />
                <circle cx="60" cy="32" r="4" fill="#818cf8" />
                <circle cx="60" cy="78" r="4" fill="#818cf8" />

                {/* Secondary connections */}
                <path d="M35,45 L25,38" stroke="#a78bfa" strokeWidth="0.5" opacity="0.6" />
                <path d="M85,45 L95,38" stroke="#a78bfa" strokeWidth="0.5" opacity="0.6" />
                <path d="M35,65 L25,72" stroke="#a78bfa" strokeWidth="0.5" opacity="0.6" />
                <path d="M85,65 L95,72" stroke="#a78bfa" strokeWidth="0.5" opacity="0.6" />
              </g>

              {/* Microphone icon at bottom */}
              <g transform="translate(52, 88)">
                <rect x="4" y="0" width="8" height="14" rx="4" fill="none" stroke="#60a5fa" strokeWidth="1.5" />
                <path d="M0,10 Q0,18 8,18 Q16,18 16,10" fill="none" stroke="#60a5fa" strokeWidth="1.5" />
                <line x1="8" y1="18" x2="8" y2="24" stroke="#60a5fa" strokeWidth="1.5" />
                <line x1="4" y1="24" x2="12" y2="24" stroke="#60a5fa" strokeWidth="1.5" />
              </g>

              {/* Animated pulse rings */}
              <circle cx="60" cy="55" r="42" fill="none" stroke="#60a5fa" strokeWidth="0.5" opacity="0.3" className="animate-ping" style={{ animationDuration: '2s' }} />
            </svg>
          </div>

          {/* AI Interviewer Label */}
          <div className="absolute -bottom-12 left-1/2 transform -translate-x-1/2 whitespace-nowrap">
            <span className="text-blue-400 text-sm font-medium tracking-wider">AI INTERVIEWER</span>
          </div>
        </motion.div>
      </div>

      {/* Floating particles effect */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-blue-400/50 rounded-full"
            initial={{
              x: Math.random() * 400,
              y: Math.random() * 400,
              opacity: 0,
            }}
            animate={{
              y: [null, -80],
              opacity: [0, 0.8, 0],
            }}
            transition={{
              duration: 4 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 3,
              ease: 'linear',
            }}
          />
        ))}
      </div>
    </div>
  );
};

const LivePreviewPage = () => {
  const navigate = useNavigate();
  const { state, actions } = useInterview();
  const { stream, isActive, error: mediaError, requestMedia } = useMedia();
  const { isOnline, quality } = useNetwork();

  const [isStarting, setIsStarting] = useState(false);
  const [toast, setToast] = useState(null);

  // Check if media stream is available (should already exist from CameraCheckPage)
  useEffect(() => {
    console.log('LivePreview: Checking media stream', {
      hasStream: !!stream,
      hasStateStream: !!state.mediaStream,
      mediaState: state.mediaState,
      streamActive: stream?.active,
      videoTracks: stream?.getVideoTracks().length,
      videoTrackState: stream?.getVideoTracks()[0]?.readyState
    });

    // If no stream exists, redirect back to camera check
    // User must go through camera check first before reaching this page
    if (!state.mediaStream && !stream) {
      console.log('LivePreview: No stream found, redirecting to camera check');
      actions.setPhase(INTERVIEW_PHASES.CAMERA_CHECK);
      navigate(ROUTES.CAMERA_CHECK, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Text content (Arabic only)
  const text = {
    title: 'البث المباشر',
    subtitle: 'أنت الآن مباشر',
    continue: 'تابع',
    stop: 'توقف',
    cameraActive: 'الكاميرا نشطة',
    micActive: 'الميكروفون نشط',
    networkGood: 'الاتصال جيد',
    networkPoor: 'الاتصال ضعيف',
    networkOffline: 'غير متصل',
    startingInterview: 'جاري بدء المقابلة...',
  };

  // Handle continue to interview - starts directly without confirmation popup
  const handleContinue = async () => {
    if (!isOnline) {
      setToast({
        type: 'error',
        message: 'يرجى التحقق من اتصالك بالإنترنت',
      });
      return;
    }

    setIsStarting(true);

    try {
      // Simulate API call to start interview session
      await new Promise(resolve => setTimeout(resolve, 1500));

      // Get candidateId from context OR sessionStorage (fallback)
      const candidateId = state.interviewData?.candidateId || sessionStorage.getItem('candidateId');

      console.log('LivePreview: Navigating to interview with candidateId:', candidateId);
      console.log('LivePreview: Context candidateId:', state.interviewData?.candidateId);
      console.log('LivePreview: SessionStorage candidateId:', sessionStorage.getItem('candidateId'));

      // Update phase and navigate
      actions.setPhase(INTERVIEW_PHASES.INTERVIEW);
      actions.setInterviewData({
        interviewStartTime: Date.now(),
      });

      // Navigate with candidateId as URL parameter
      if (candidateId) {
        navigate(`${ROUTES.INTERVIEW}?candidate_id=${candidateId}`);
      } else {
        console.error('LivePreview: No candidateId found in context or sessionStorage');
        setToast({
          type: 'error',
          message: 'خطأ: لم يتم العثور على معرف المرشح',
        });
        setIsStarting(false);
        // Navigate back to proceed page
        setTimeout(() => {
          navigate(ROUTES.PROCEED);
        }, 2000);
      }
    } catch (error) {
      console.error('Failed to start interview:', error);
      setToast({
        type: 'error',
        message: 'فشل بدء المقابلة. يرجى المحاولة مرة أخرى.',
      });
      setIsStarting(false);
    }
  };

  // Handle stop/cancel
  const handleStop = () => {
    // Navigate back to camera check
    actions.setPhase(INTERVIEW_PHASES.CAMERA_CHECK);
    navigate(ROUTES.CAMERA_CHECK);
  };

  // Network quality indicator
  const getNetworkStatus = () => {
    if (!isOnline) return { text: text.networkOffline, color: 'text-red-500', icon: WifiOffIcon };
    if (quality === 'excellent' || quality === 'good') return { text: text.networkGood, color: 'text-green-500', icon: WifiIcon };
    return { text: text.networkPoor, color: 'text-yellow-500', icon: WifiIcon };
  };

  const networkStatus = getNetworkStatus();

  return (
    <div
      className="min-h-screen bg-surface-100"
      dir="rtl"
    >
      {/* Toast Notification */}
      {toast && (
        <Toast
          type={toast.type}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <h1 className="text-3xl font-bold text-gray-900 mb-2 font-display">
            {text.title}
          </h1>
          <p className="text-gray-500">
            {text.subtitle}
          </p>
        </motion.div>

        {/* Video Grid */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="grid md:grid-cols-2 gap-8 mb-12"
        >
          {/* AI Avatar */}
          <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-strong">
            <LivePreviewAvatar />
          </div>

          {/* User Video */}
          <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-strong relative">
            {stream ? (
              <VideoPreview
                stream={stream}
                isActive={state.cameraEnabled}
                showStatus={true}
                mirrored={true}
                className="w-full h-full"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gray-900">
                <div className="text-center">
                  <Spinner size="lg" className="mb-4" />
                  <p className="text-white">جاري تحميل الكاميرا...</p>
                </div>
              </div>
            )}

            {/* Status badge */}
            {stream && (
              <div className="absolute bottom-4 start-4">
                <div className="flex items-center gap-2 px-3 py-1.5 bg-black/50 backdrop-blur-sm rounded-full">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-white text-sm font-medium">{text.cameraActive}</span>
                </div>
              </div>
            )}
          </div>
        </motion.div>

        {/* Status Indicators */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex justify-center gap-8 mb-12"
        >
          {/* Camera Status */}
          <div className="flex items-center gap-2 text-green-600">
            <CameraIcon size={20} />
            <span className="text-sm font-medium">{text.cameraActive}</span>
          </div>
          
          {/* Mic Status */}
          <div className="flex items-center gap-2 text-green-600">
            <MicrophoneIcon size={20} />
            <span className="text-sm font-medium">{text.micActive}</span>
          </div>
          
          {/* Network Status */}
          <div className={`flex items-center gap-2 ${networkStatus.color}`}>
            <networkStatus.icon size={20} />
            <span className="text-sm font-medium">{networkStatus.text}</span>
          </div>
        </motion.div>

        {/* Action Buttons */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="flex justify-center gap-4"
        >
          <Button
            variant="primary"
            size="lg"
            onClick={handleContinue}
            disabled={isStarting || !isOnline}
            className="min-w-[200px]"
          >
            {isStarting ? (
              <span className="flex items-center gap-2">
                <Spinner size="sm" />
                {text.startingInterview}
              </span>
            ) : (
              <span className="flex items-center gap-2">
                <PlayIcon size={20} />
                {text.continue}
              </span>
            )}
          </Button>
          
          <Button
            variant="secondary"
            size="lg"
            onClick={handleStop}
            disabled={isStarting}
            className="min-w-[200px]"
          >
            <span className="flex items-center gap-2">
              <StopIcon size={20} />
              {text.stop}
            </span>
          </Button>
        </motion.div>
      </div>

      {/* Loading Overlay */}
      {isStarting && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center"
        >
          <div className="bg-white rounded-2xl p-8 text-center shadow-strong">
            <Spinner size="lg" className="mb-4" />
            <p className="text-gray-700 font-medium">{text.startingInterview}</p>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default LivePreviewPage;
